import {
  Button,
  Icon,
  InformationCard,
  Text,
  Unifree,
  useIsMobile,
  YesObject
} from '@yes.technology/react-toolkit'
import { FocusEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import 'react-medium-image-zoom/dist/styles.css'

import WebcamCapture from '../../../../shared/WebcamCapture'
import useUploadS3, {
  UploadFileResult
} from '../../../hooks/useUploadS3/useUploadS3'
import useDocument from '../../../../shared/hooks/useDocument'
import FileUploader from '../../../../shared/FileUploader'
import ViewPhoto from '../../../../shared/ViewPhoto'
import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import { useAppDispatch } from 'state/store'
import { useDocumentURL } from 'modules/document/hooks'
import useIsOpen from 'modules/shared/hooks/useIsOpen'
import PhotoMarkingModal, { PhotoMark } from './PhotoMarkingModal'
import { fileToBase64 } from 'utils/file/file'
import MarkList from './MarkList'
import { Values } from './types'
import MarkedImage from './MarkedImage'
import fetchFilteraction from 'modules/filteraction/fetchFilteraction'
import { useBlueprintsContext } from 'modules/interaction/hooks'

type ActivityMarkPhotoProps = {
  uuid?: string
  inputValue: string
  required?: boolean
  onChange: (value: string) => void
  label: string
  maxSizeInBytes?: number
  categoriesFilteraction: string
  isFullscreenMarking?: boolean
  markingPromptTitle?: string
  categorySelectionModal?: { des: string; complement?: string }
}

const ActivityMarkPhoto = ({
  uuid,
  inputValue,
  onChange,
  label,
  required,
  maxSizeInBytes,
  categoriesFilteraction,
  isFullscreenMarking = false,
  markingPromptTitle,
  categorySelectionModal
}: ActivityMarkPhotoProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { executeBlueprints } = useBlueprintsContext()

  const { createOrUpdateDocument } = useDocument()
  const {
    isOpen: isWebcamOpen,
    open: openWebcam,
    close: closeWebcam
  } = useIsOpen()

  const {
    isOpen: isMarkingModalOpen,
    open: openMarkingModal,
    close: closeMarkingModal
  } = useIsOpen()

  const {
    isOpen: isMarkingPromptOpen,
    open: openMarkingPrompt,
    close: closeMarkingPrompt
  } = useIsOpen()

  const { uploadFile } = useUploadS3()

  const [view, setView] = useState(false)
  const [values, setValues] = useState<Values>()
  const [file, setFile] = useState<string | null>(null)
  const [categoryObjectclass, setCategoryObjectclass] = useState<string>('')

  useEffect(() => {
    if (inputValue !== '') {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  useEffect(() => {
    const fetchCategoryObjectclass = async () => {
      const filteraction = await fetchFilteraction(categoriesFilteraction)

      const objectclassId = (
        filteraction?.filter_action_objectclass as YesObject[]
      )?.[0]?.uuid

      setCategoryObjectclass(objectclassId || '')
    }

    fetchCategoryObjectclass()
  }, [categoriesFilteraction, setCategoryObjectclass])

  const onSuccessUploadFile = async ({ url, content }: UploadFileResult) => {
    const values = inputValue && JSON.parse(inputValue)
    const { document, statusDes } = await createOrUpdateDocument({
      uuid: values?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    const newValues = JSON.stringify({
      ...values,
      uuid: document?.uuid,
      uuid_status: statusDes,
      des: document?.des,
      contentType: content.type,
      size: content.size,
      url
    })

    setFile(await fileToBase64(content))

    onChange(newValues)

    executeBlueprints({
      type: 'activity_changed',
      activityValue: newValues,
      activityModelUuid: uuid
    })

    dispatch({
      type: 'api/RECEIVED',
      data: ''
    })

    openMarkingPrompt()
  }

  const handlePromptConfirm = () => {
    closeMarkingPrompt()
    openMarkingModal()
  }

  const capturePhoto = (img: string) => {
    dispatch({
      type: 'api/REQUESTED',
      data: ''
    })

    closeWebcam()

    uploadFile({
      file: img,
      fileType: 'base64'
    })
      .then(onSuccessUploadFile)
      .catch(onErrorUploadFile)
  }

  const onChangeDescription = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const saveDescription = (event: FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !documentURL) {
      return
    }

    setTimeout(async () => {
      const values = JSON.parse(inputValue)
      const { document, statusDes, statusUuid } = await createOrUpdateDocument({
        uuid: values.uuid,
        des: event.target.value
      })

      onChange(
        JSON.stringify({
          ...values,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusUuid,
          statusDes: statusDes
        })
      )
    }, 500)
  }

  const onErrorUploadFile = () => {
    dispatch({
      type: 'api/RECEIVED',
      data: ''
    })
  }

  const uploadLocalFile = (file: File) => {
    dispatch({
      type: 'api/REQUESTED',
      data: ''
    })

    uploadFile({
      file
    })
      .then(onSuccessUploadFile)
      .catch(onErrorUploadFile)
  }

  const clearData = () => {
    onChange('')
    setValues({})
    executeBlueprints({
      type: 'activity_changed',
      activityValue: '',
      activityModelUuid: uuid
    })
  }

  const handleSaveMarks = (marks: PhotoMark[]) => {
    const currentValues = inputValue ? JSON.parse(inputValue) : values

    onChange(
      JSON.stringify({
        ...currentValues,
        marks,
        categoryObjectclass
      })
    )

    closeMarkingModal()
  }

  const { documentURL } = useDocumentURL({ s3PrivateURL: values?.url })

  const marks = useMemo(() => values?.marks || [], [values?.marks])

  const isMobile = useIsMobile()

  return (
    <>
      {!isWebcamOpen && (
        <div style={{ display: 'grid', rowGap: '8px' }}>
          <Unifree
            label={t('activity-document-photo.labels.des')}
            value={values?.des || ''}
            onChange={onChangeDescription}
            onBlur={saveDescription}
            data-testid='description-field'
            disabled={!documentURL}
            placeholder={t('activity-document-photo.placeholders.des')}
          />
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1'>
              <Unifree
                label={label || t('activity-document-photo.labels.uuid-foto')}
                disabled={true}
                value={values?.uuid || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.uuid-foto'
                )}
              />
            </div>
            {documentURL ? (
              <Button
                onClick={() => setView(true)}
                variant='secondary'
                aria-label={t('activity-document-photo.actions.view')}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Visualization' icSize='semiSmall' />
              </Button>
            ) : (
              <Button
                onClick={openWebcam}
                aria-label={t('activity-document-photo.actions.take-photo')}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='TakePhoto' icSize='semiSmall' />
              </Button>
            )}
            {!documentURL && (
              <FileUploader
                onChange={uploadLocalFile}
                accept='image/*'
                variant='secondary'
                maxSizeInBytes={maxSizeInBytes}
              />
            )}
            {documentURL && (
              <Button
                variant='secondary'
                onClick={clearData}
                aria-label={t('activity-document-photo.actions.clear')}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Cleaner' icSize='semiSmall' />
              </Button>
            )}
          </div>
          <Unifree
            label={t('activity-document-photo.labels.status')}
            disabled={true}
            value={values?.statusDes || ''}
            required={required}
          />
          <div className='row'>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.content-type')}
                disabled={true}
                value={values?.contentType || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.content-type'
                )}
              />
            </div>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.size')}
                disabled={true}
                value={values?.size ? formatFileSize(Number(values.size)) : ''}
                required={required}
                placeholder={t('activity-document-photo.placeholders.size')}
              />
            </div>
          </div>

          <div
            style={{ maxHeight: '250px', overflow: 'hidden' }}
            className='w-100 d-flex justify-content-center align-items-center'
          >
            {documentURL ? (
              <MarkedImage
                src={documentURL}
                des={values?.des || ''}
                marks={marks}
              />
            ) : (
              <div className='w-100 webcam-no-image rounded'>
                {t('activity-document-photo.no-image')}
              </div>
            )}
          </div>

          <MarkList marks={marks} />
        </div>
      )}

      {isWebcamOpen && (
        <WebcamCapture
          overlayImage={null}
          onSuccess={capturePhoto}
          onCancel={closeWebcam}
        />
      )}

      {view && documentURL && (
        <ViewPhoto
          onCancel={() => setView(false)}
          image={documentURL}
          marks={marks}
        />
      )}

      <InformationCard
        isOpen={isMarkingPromptOpen}
        title={
          markingPromptTitle ??
          t('activity-document-photo.mark-photo.prompt-title')
        }
        close={closeMarkingPrompt}
        style={{
          width: '100%',
          maxWidth: isMobile ? '90vw' : '400px',
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          margin: 0,
          zIndex: 1050
        }}
      >
        <Text variant='label-02'>
          {t('activity-document-photo.mark-photo.prompt-subtitle')}
        </Text>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button onClick={handlePromptConfirm}>
            {t('activity-document-photo.mark-photo.prompt-confirm')}
          </Button>
          <Button variant='secondary' onClick={closeMarkingPrompt}>
            {t('activity-document-photo.mark-photo.prompt-skip')}
          </Button>
        </div>
      </InformationCard>

      {file && (
        <PhotoMarkingModal
          isOpen={isMarkingModalOpen}
          image={file}
          categoriesFilteraction={categoriesFilteraction}
          parentPath={[]}
          initialMarks={marks}
          onSave={handleSaveMarks}
          onCancel={closeMarkingModal}
          isFullscreen={isFullscreenMarking}
          categorySelectionModal={categorySelectionModal}
        />
      )}
    </>
  )
}

export default ActivityMarkPhoto
